import {
  makeAddressArraySync,
  makeAddressSync,
  ProtocolConfig,
} from "@silo/types";
import { satisfies } from "effect/Function";
import { TOKEN_ADDRESS } from "../token-address";
import { MARKET_ADDRESS } from "../market-address";


export const arbitrum = satisfies<ProtocolConfig>()({
  firstBridgeSymbol: "ETH",
  secondBridgeSymbol: "USDC.e",
  marketWhitelist: new Set([
    MARKET_ADDRESS.arbitrum.MAGIC,
    MARKET_ADDRESS.arbitrum.GMX,
    MARKET_ADDRESS.arbitrum.UNI,
    MARKET_ADDRESS.arbitrum.GNS,
    MARKET_ADDRESS.arbitrum.ARB,
    MARKET_ADDRESS.arbitrum.WBTC,
    MARKET_ADDRESS.arbitrum.WSTETH,
    MARKET_ADDRESS.arbitrum.rETH,
    MARKET_ADDRESS.arbitrum.GRAIL,
    MARKET_ADDRESS.arbitrum.PENDLE,
    MARKET_ADDRESS.arbitrum.WINR,
    MARKET_ADDRESS.arbitrum.weETH,
    MARKET_ADDRESS.arbitrum.ezETH,
    MARKET_ADDRESS.arbitrum.PTezETH,
    MARKET_ADDRESS.arbitrum.WOETH,
    MARKET_ADDRESS.arbitrum.gmETH,
  ]),
  pinnedMarkets: [
    MARKET_ADDRESS.arbitrum.gmETH,
    MARKET_ADDRESS.arbitrum.weETH,
    MARKET_ADDRESS.arbitrum.ezETH,
  ],
  markets: {
    [MARKET_ADDRESS.arbitrum.PTezETH_SEPT2024]: {
      points: {
        firstBridge: {
          collateral: [{ _tag: "renzo", label: "1x Renzo" }],
        },
      },
      depositMode: {
        base: "protectedOnly",
      },
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.PTweETH_SEPT2024]: {
      points: {
        firstBridge: {
          collateral: [{ _tag: "etherFi", label: "1x EtherFi" }],
        },
      },
      depositMode: {
        base: "protectedOnly",
      },
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.PTweETH_DEC2024]: {
      depositMode: {
        base: "protectedOnly",
      },
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.PTrsETH_SEPT2024]: {
      points: {
        firstBridge: {
          collateral: [{ _tag: "kelp", label: "1x Kelp Miles" }],
        },
      },
      depositMode: {
        base: "protectedOnly",
      },
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.weETH]: {
      points: {
        base: {
          collateral: [
            { _tag: "eigenLayer" },
            { _tag: "etherFi", label: "2x EtherFi" },
          ],
        },
        firstBridge: {
          collateral: [
            { _tag: "etherFi", label: "1x EtherFi" },
            { _tag: "turtle" },
          ],
        },
      },
    },
    [MARKET_ADDRESS.arbitrum.ezETH]: {
      points: {
        base: {
          collateral: [
            { _tag: "eigenLayer" },
            { _tag: "renzo", label: "2x Renzo" },
          ],
        },
        firstBridge: {
          collateral: [
            { _tag: "renzo", label: "1x Renzo" },
            { _tag: "turtle" },
          ],
        },
      },
    },
    [MARKET_ADDRESS.arbitrum.PTweETH]: {
      points: {
        firstBridge: {
          collateral: [{ _tag: "etherFi", label: "1x EtherFi" }],
        },
      },
      depositMode: {
        base: "protectedOnly",
      },
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.PTrsETH]: {
      isDebtAprFixed: {
        firstBridge: true,
      },
      depositMode: {
        base: "protectedOnly",
      },
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.PTrsETH_DEC2024]: {
      depositMode: {
        base: "protectedOnly",
      },
    },
    [MARKET_ADDRESS.arbitrum.SILO]: {
      depositMode: {
        base: "protectedOnly",
      },
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.Y2K]: {
      depositMode: {
        base: "protectedOnly",
      },
    },
    [MARKET_ADDRESS.arbitrum.PTUSDe]: {
      depositMode: {
        base: "protectedOnly",
      },
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.PTezETH]: {
      depositMode: {
        base: "protectedOnly",
      },
      warningConfig: {
        warningKey: "ezEth-dex",
        scope: "singleMarket",
      },
    },
    [MARKET_ADDRESS.arbitrum.WOETH]: {
      depositMode: {
        base: "protectedOnly",
      },
    },
    [MARKET_ADDRESS.arbitrum.GRAI]: {
      depositMode: {
        base: "protectedOnly",
      },
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.PEAS]: {
      depositMode: {
        base: "protectedOnly"
      },
    },
    [MARKET_ADDRESS.arbitrum.RAM]: {
      depositMode: {
        base: "protectedOnly",
      },
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.ptUSDE_NOV2024]: {
      depositMode: {
        base: "protectedOnly",
      },
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.uniETH]: {
      points: {
        base: {
          collateral: [{ _tag: "bedrock", label: "4x Diamonds" }],
        },
      },
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.tBTC]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.stEUR]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.WOO]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.JOE]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.ETHplus]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.ORDER]: {
      depositMode: {
        base: "protectedOnly",
      },
    },
    [MARKET_ADDRESS.arbitrum.OHM]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.UMAMI]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.rDPX]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.LUSD]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.CRV]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.PLS]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.arbitrum.wUSDM]: {
      labelAsDeprecated: true,
    }, 
    [MARKET_ADDRESS.arbitrum.PTweETH_JUN2025]: {
      depositMode: {
        base: "protectedOnly",
      },
    },
    [MARKET_ADDRESS.arbitrum.PTrsETH_JUN2025]: {
      depositMode: {
        base: "protectedOnly",
      },
    },
    [MARKET_ADDRESS.arbitrum.gmETH]: {
      depositMode: {
        base: "protectedOnly",
      },
    },
  },
  nativeRewards: {
    siloIncentivesControllerAddress: makeAddressSync(
      "0xbDBBf747402653A5aD6F6B8c49F2e8dCeC37fAcF"
    ),
    tokenAddress: TOKEN_ADDRESS.arbitrum.SILO,
    symbol: "SILO",
    decimals: 18,
    historicalShareTokens: {
      [TOKEN_ADDRESS.arbitrum.SILO]: makeAddressArraySync([
        //sARB
        "0x0efe65B6f24d46af9Ce261EE01b1f1A32202e22F",
        //sUSDC-ARB
        "0x55ADE3B74abef55bF379FF6Ae61CB77a405Eb4A8",
        //sWETH-ARB
        "0x7Fd52Da78f579Db67CdD09e5D8981ecFCCfc8bc9",
        //sUSDC-GMX
        "0x96E1301bd2536A3C56EBff8335FD892dD9bD02dC",
        //sUSDC-GNS
        "0x5d878E9BFAb130398c5944d328b3Fa7DD97A2bcf",
        //sUSDC-GRAIL
        "0xcaf50473fe603e120a2f2a87715217cc3a4061b9",
        //sWETH-GRAIL
        "0xb1d88848E0A6a29e6D333C5625CdE476D6e96786",
        //sUSDC-MAGIC
        "0x7D33A7CEaA886CB4A0cbf4F823C80c84EfDfC985",
        //sPENDLE
        "0x4E873117631835524CDa0c3cAB936F1CD01ecF33",
        //sUSDC-PENDLE
        "0x5c4b8981995184a897a260dbd0424d5f8a8e339d",
        //sWETH-PENDLE
        "0x26F2D64Ac6676ec8673B845A1DC549b17A334c7F",
        //sPREMIA
        "0x0C2ab66CDBbFf36510A929C531Cb5C1d57EDB362",
        //sUSDC-PREMIA
        "0xF76e081B3CD26B1696464B5C534a2FE62086E2BC",
        //sWETH-PT-eETH
        "0x9d0c7DAFE3b34B51010DB522b9F17455Fa9Cea7C",
        //sUSDC-PT-eETH
        "0x066B47937b83BC11E84200D7d37B05C127Cc5cB7",
        //sWETH-PT-ezETH
        "0x71453D859918A1D59354324c2A70d32e53b97f67",
        //sUSDC-PT-ezETH
        "0xdF6D0A40f61E3d2bef4C3e8222c76eB6e0bC6507",
        //sWETH-PT-rsETH
        "0xA7f4584b853c1bB8ebE2245c2A0527DEFD12727A",
        //sUSDC-PT-rsETH
        "0x1E6fD3251c949E1708a5277Faa773CC88E799B99",
        //sUSDC-PT-USDe
        "0x712238A91f01849EAdFAa8662B6fb94705e61063",
        //sWETH-PT-USDe
        "0x0698E028F1486b9FcDa301975014F85d0a76FE43",
        //sUSDC-RDNT
        "0xe9b35c753b6ec9b5a4bbd8c385d16cdb19517185",
        //sUSDC-rETH
        "0x5cb118deb1a3daef808f414bae66576ebda5e735",
        //sWETH-rETH
        "0xfc0eff4a5f7dcf4fd1C0a9Cc4Bc840882A391ECD",
        //sUSDC-SILO
        "0x00CcE18E859aCdDe4c949852E67c20510F2768a5",
        //sWETH-SILO
        "0x2a8a7F6Fb67AC920d81b242dbC388d51d40d365F",
        //swBTC - currently causing claims to revert
        // "0x29Aa259659402c6280620048e976537ff9AFFdbB",
        //sUSDC-WBTC
        "0xFb6DE7D8Ca3Ec3396bB1Cc53adDEf1F26468055B",
        //sWETH-WBTC
        "0x7F660755Bf6924BD7C33059d66bb6609F511Eb5c",
        //sUSDC-woETH
        "0xb02a83A156BaD7dEdeaDB9dD289d8E54f49Ce6c2",
        //sWETH-woETH
        "0x6c9DaDB5a47c7B146275420cb393Fdc4425AB430",
        //sweETH
        "0xbbA48ae564D029CD809030Fa6FA4Da00Ae2B9C28",
        //sWETH-weETH
        "0x849Da604C863F251FB6AC5e1aB537075198CCbD3",
        //sUSDC-weETH
        "0xD57DFC722dae39D7800173dfDbd1c43cbc9d37aa",
        //swstETH
        "0x2A6Cd33AE19e078c3Dd0d77d976c451658330392",
        //sezETH
        "0x77e4eb57f42a76eB18342eA0D1D94D36f8eacEF8",
        //sUSDC-ezETH
        "0xD5eaC1258d6FDA41E1C99b3a0a89C7d8929C37A2",
        //sWETH-wstETH
        "0xC46C5DE6eCa1CA6dc3f8c5d3ce863aE326F4b2CF",
        //sUSDC-wstETH
        "0x713fc13caab628f116bc34961f22a6b44ad27668",
        //sETH+
        "0x3d748240644358D9102905d1e8EDC6C7bE9755b6",
        //sETH-ETH+
        "0x95633979ae07b857a5A03BbA349EAE891E27fB5E",
        //sUSDC-ETH+
        "0x95f391B6C2ba4e1Be7f0FD849643687B16CF7cE9",
        //suniETH
        "0x9b3B592a544041EaFDc2Aa502A318Af630a32E10",
        //sETH-uniETH
        "0x82804e3d7914092899B0A76E5308799cc276fAe6",
        //sUSDC-uniETH
        "0x32456e03ef868B78D00C02b706a6C0Ae997b6955",
        //spRAM
        "0x06b0d68Fa81B1f0963a8EFb601cA2C58d1f59B19",
        //sETH-RAM
        "0x95164bd7764930187A81792DaFCaC3574Be2CCaB",
        //sUSDC-RAM
        "0xD7fB8a9254fB6ab8Ba7DC447D96E4f08D37DF01D",
        //spwUSD+
        "0x09a94f9F4774bf700A0D50324a4edB2103579448",
        //sETH-wUSD+
        "0xc74F466B8597D88d418F5666e2B64db85eD1C16B",
        //sUSDC-wUSD+
        "0xcA751A650B5A5C0E6e204cFaE21F2F3c8C35e9A4",
        //sWETH-RDNT
        "0xb629EF34358395E2E68756Dc1Bb4EBB99a943D7d",
        //spPEAS
        "0xdaCD73C62Feb1e1656897d7279909c2485be36Fc",
        //sETH-PEAS
        "0xA071de4E0ac29B1E6F68Ea3CD98E4C092a224D09",
        //sUSDC-PEAS
        "0xB8eC604C2F3ca849f4a009585A0f9D5e1E06B2Bc",
        //dWETH-PT-ezETH-SEP2024
        "0xaB8491f8f1617F1B903314Bdca9Cc5A430CD383C",
        //dWETH-PT-eETH-SEP2024
        "0xf6FBDce5639378BC4E8a44a4D70c0EFBFbF58D13",
        //dWETH-weETH
        "0xF5d1a10D48EEf195Bb40D2ba707c4a3a92Cc2B1c",
        //dUSDC-weETH
        "0xD578684C437E070fd702C20d527d40085eD2042c",
        //dWETH-ezETH
        "0x4e8c5aA37b30d3eC4e6699AEaf5BBbae4dc781e3",
        //dUSDC-ezETH
        "0x523075556fc6439Bbe9239b086D3E6F0C3e47B25",
        //dWETH-ARB
        "0x86B25620401413d99c17DCebd2885477cb1737e1",
        //dUSDC-ARB
        "0x985BBaf88f91C39ABE78F83BA6f5688E926B6537",
        //dWETH-WBTC
        "0x0070f71fcc0F21a976F21a81d770a7D5ceb5E1E1",
        //dUSDC-WBTC
        "0x08A5fD35f8E561853978b6dC1AFF3CE48973E088",
        //dWETH-wstETH
        "0x16063D1eD0bd95A1E8a20023e79080f5B32e44e5",
        //dUSDC-wstETH
        "0x73b819E53d4f64AA51c1Ee3ad40C7D769f35036a",
        //dUSDC-wUSD+
        "0xdd892c828046a0735a1320b928571a41e3610CdD",
        //spPT-weETH-26SEP2024
        "0xDF8D15a0d87F352A1733E006eE40524faefb0D0E",
        //spPT-ezETH-26SEP2024
        "0x14a71C294E91d2Ca14966492b7fceD5Cd75fB083",
        //spPT-rsETH-26SEP2024
        "0x7F09966F22aab018024E7fA31b5505aCA3689108",
        //sWETH-ezETH
        "0xe7F05eFb2A1572e96428bbfE5D1e4c9E3689b2ec",
        //usdc-PT-rsETH
        "0x4a7FF2901a8577363eE6ac75077bc3EE67A93C98",
        //sWETH-tBTC
        "0xBaaC6e1e821C9498b2494ae2fE6F42063f7B232C",
        //sUSDC-tBTC
        "0x0258cddDc09703B5f4889F6A1D4bA587Be5580C4",
        //spgUSDC
        "0xb8a1B4428D09e9E977F7587A931217c0871D1786",
        //sETH-gUSDC
        "0x4a4B0d051419FAA4e34Ef1C51A8c25a11849aAA8",
        //sUSDC-gUSDC
        "0x09D82D491329567a29982Ee8f284BFE12802FAdE",
        //spPT-sUSDe-NOV2024
        "0xe111B404F4Efa6d8556f7E164310F5b97Ab247fD",
        //sWETH-PT-USDe-NOV2024
        "0x0005523e1dD56fBC46cef715fE6f67Af5BB1fD4c",
        //sUSDCe-PT-USDe-NOV2024
        "0x714754de5301f2F2b7Ee26E94B42E978E7f56113",
        //sDLCBTC
        "0x378107DA2143cf01cFf9f68Ed40e45872c60dD51",
        //sweth-dlcBTC
        "0x6700AAA7FaF636F537Fb9607A8b8F2ec3cd07CE1",
        //usdc-dlcBTC
        "0xa3663AD76155d7f87136f81A51F12d64A0BE7bf5",
        //swUSDM
        "0x6cbf9f52fb186Ba2D1E9c0E1D2aF8CcecD606923",
        //sWETH-wUSDM
        "0x64fAEF7D5C43BD037AafD82428B408450b6fc4Fd",
        //sUSDC-wUSDM
        "0x998A675b010CFd2Ce73d30120842aCD0538C4168",
        //swUSD+
        "0x5E40539a9B07eAAd8538231753f8bC4FD0e36656",
        //spPT-weETH
        "0xAA8bFB79491748079210BF03A4f91144197bA0CB",
        //sETH-PT-weETH
        "0xAA8bFB79491748079210BF03A4f91144197bA0CB",
        //sUSDC-PT-weETH
        "0x914C6dCC7F10C5729e5182C44276D19b030Af423",
        //spPT-rsETH
        "0x9f5a8e95D092958530e4738F05bCF8D445b731c9",
        //sETH-PT-rsETH
        "0x603062d7cCCc530266eC01A0d3DDaF87015c156e",
        //sUSDC-PT-rsETH
        "0x1333aef6e089DC9B3C5744E490E06fdf045ba388",
        // spPT-weETH-26DEC2024
        "0xc7A738953959C335830054Ac95B1661BdF33fd36",
        //spORDER
        "0x8d81fe13788474aeD860cac4D73C0b942612d656",
        //sUSDC-ORDER
        "0x83eA3752bD58cf35B0829d9897bb9C50847f0490",
        //sWETH-ORDER
        "0x174F101F44D08607A1535656BE6C802dC342f122",
        //spPT-weETH
        "0x674c60e7d92248869897EdEE87d43ED717a60691",
        //sETH-PT-weETH
        "0xfDF152025a160aDD5e81BC2b2bDa27D1D8e0204C",
        //sUSDC-PT-weETH
        "0x59B5CA96c03D3E1BDd7B53b1E8bfFC54A8AA238B",
        //spPT-rsETH
        "0x6249bC9589c58fae5D75f30C34C90330F4C69423",
        //sETH-PT-rsETH
        "0xa83263FDcd3AD845F94fc9c34C80A9c77824C0FE",
        //sUSDC-PT-rsETH
        "0x67B1b89a8b72be48EA1b42BCd838Be371d3EB854",
        //spgmETH
        "0xdf53113F989E9469465DAdb017FE68FD69bC6863",
        //sETH-gmETH
        "0x5B2108D16d04472eD47EcD6fC17d901B9E634e69",
        //sUSDC-gmETH
        "0xd94260A3c1D0b96f10FD27011456c16FE8Ce2f78",
        //sTANGO
        "0x1256EfC742c5F7659A05d353821fC5488b71C1a7",
        //sETH-TANGO
        "0xB01b3255936AE8B6404a18cBa4C416E86f201Fca",
        //sUSDC-TANGO
        "0xcaA6584865F759cAdC08aF3e784E4caA28484e56",
      ]),
      [TOKEN_ADDRESS.arbitrum.ARB]: makeAddressArraySync([]),
    },
  },
} as const);
